import type { KeyboardEvent, MouseEvent } from "react";
import { Card, CardContent } from "@repo/ui";
import { formatPriceRange } from "@/lib/queries/products";
import { buildConditionBadges, type ConditionCriteria } from "@/lib/conditions";
import { isProductOutOfStock } from "./product-utils";
import { Trash2 } from "lucide-react";

export interface ProductCardData {
  id: number;
  catalogProductId?: number;
  brandCatalogId?: number;
  name: string;
  image?: string | null;
  priceMin: number;
  priceMax: number;
  currencyCode: string;
  totalInventory?: number;
  tracksInventory?: boolean;
  category?: string | null;
  productType?: string | null;
  collectionNames?: string[];
  collectionIds?: number[];
  /**
   * Underlying collection.externalId values for this product (Shopify GID or
   * CSV slug). EPD-8597 — canonical match key against condition.collections[].id.
   */
  collectionExternalIds?: string[];
  vendor?: string | null;
  tags?: string[] | null;
  isFeatured?: boolean;
  hidePriceOnCard?: boolean;
  /**
   * Per-variant stock, when the source payload carries it. Lets the card evaluate
   * stock the same way the detail view does instead of trusting `totalInventory`,
   * which is a *signed* sum an oversold variant can drag below zero (EPD-10274).
   */
  variants?: ProductVariantStock[];
}

/** Minimal per-variant shape needed to evaluate stock. */
export interface ProductVariantStock {
  inventoryQuantity?: number | null;
  sellWhenOutOfStock?: boolean;
}

interface ProductCardProps {
  product: ProductCardData;
  onClick: (product: ProductCardData) => void;
  showPrice?: boolean;
  /** When false, suppresses the Vendor badge. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, suppresses Tag badges. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  /** Condition criteria for badge highlighting. When provided, matching metadata is shown as teal badges. */
  conditionCriteria?: ConditionCriteria;
  /** Total quantity of this product in the cart (across all variants). When > 0, card gets a highlight and badge. */
  cartQuantity?: number;
  /** Called when the trash icon is clicked to remove this product from the cart. */
  onRemoveFromCart?: (product: ProductCardData) => void;
  /** Minimum stock level from brand inventory settings. Products with inventory <= this threshold are OOS. */
  minimumStockLevel?: number;
}

export function ProductCard({
  product,
  onClick,
  showPrice = true,
  showVendor = true,
  showTags = true,
  conditionCriteria,
  cartQuantity = 0,
  onRemoveFromCart,
  minimumStockLevel = 0,
}: ProductCardProps) {
  const isOutOfStock = isProductOutOfStock(product, minimumStockLevel);
  const inCart = cartQuantity > 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-disabled={isOutOfStock}
      className={
        isOutOfStock
          ? "opacity-60 cursor-not-allowed overflow-hidden"
          : `cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none overflow-hidden ${inCart ? "ring-2 ring-primary border-primary" : ""}`
      }
      onClick={() => {
        if (!isOutOfStock) onClick(product);
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (!isOutOfStock && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(product);
        }
      }}
    >
      <div className="relative aspect-square bg-muted">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No image
          </div>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-medium text-sm">Out of Stock</span>
          </div>
        )}
        {inCart && !isOutOfStock && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
            {onRemoveFromCart && (
              <button
                type="button"
                aria-label="Remove from cart"
                className="flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 transition-colors"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  onRemoveFromCart(product);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold px-1.5 shadow-sm">
              {cartQuantity}
            </span>
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-medium text-sm text-foreground line-clamp-2 mb-1">
          {product.name}
        </h3>
        {showPrice && !product.hidePriceOnCard && (
          <p className="text-sm text-muted-foreground">
            {formatPriceRange(
              product.priceMin,
              product.priceMax,
              product.currencyCode,
            )}
          </p>
        )}
        {conditionCriteria && (
          <ConditionBadges
            product={product}
            criteria={conditionCriteria}
            showVendor={showVendor}
            showTags={showTags}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Compact badges showing which condition-relevant metadata this product has.
// Matching criteria are highlighted in teal.
function ConditionBadges({
  product,
  criteria,
  showVendor = true,
  showTags = true,
}: {
  product: ProductCardData;
  criteria: ConditionCriteria;
  showVendor?: boolean;
  showTags?: boolean;
}) {
  // Catalog-level `hideVendorsFromCreators` / `hideTagsFromCreators` suppress
  // the corresponding fields before badge construction (EPD-8604) — applies
  // even when a tag/vendor matches a selection rule.
  const badges = buildConditionBadges(
    {
      ...product,
      vendor: showVendor ? product.vendor : undefined,
      tags: showTags ? product.tags : undefined,
    },
    criteria,
  );

  // Only show badges that match conditions to keep cards compact
  const matching = badges.filter((b) => b.matches);
  if (matching.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1.5 max-h-[3.5rem] overflow-hidden">
      {matching.map((badge, i) => (
        <span
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`${badge.label}-${i}`}
          className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded-sm bg-teal-100 text-teal-800 border border-teal-200"
        >
          {badge.label}
        </span>
      ))}
    </div>
  );
}
